/**
 * Os alertas do cockpit pelo WhatsApp (`W-08`, ADR-052), ligados pela própria pessoa.
 *
 * O cockpit escreve primeiro: "12 passagens perderam o prazo de 24 h entre 14h e 15h". Quem
 * liga é quem recebe — o administrador vê, não liga por ninguém —, e o número sai do boletim
 * de prazos do TollVision, nunca do modelo de linguagem. Mora no painel do Pergunte ao
 * Cockpit, embaixo da linha do WhatsApp: é onde a pessoa já está quando pensa no WhatsApp.
 *
 * Fechado, ocupa uma linha ("Alertas no WhatsApp · 1 ligado"); aberto, uma caixa por alerta,
 * gravada no clique. E diz as duas coisas que evitam surpresa: nenhum alerta sai de noite, e
 * PARAR no WhatsApp desliga todos.
 */
import { useEffect, useState } from "react";

import { ErroDaApi, api } from "../api/cliente";
import type { AlertaDaPessoa, MeusAlertas } from "../api/tipos";

export function AlertasNoWhatsApp() {
  const [meus, definirMeus] = useState<MeusAlertas | null>(null);
  const [gravando, definirGravando] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    api<MeusAlertas>("/whatsbi/meus-alertas")
      .then((m) => vivo && definirMeus(m))
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, []);

  // Sem a lista (a API mais antiga, ou a resposta sem forma), nada: o painel segue sem ela.
  if (!meus || !Array.isArray(meus.alertas)) return null;
  const ligados = meus.alertas.filter((a) => a.ligado);

  async function trocar(alerta: AlertaDaPessoa, ligar: boolean) {
    if (!meus) return;
    const proximos = meus.alertas
      .filter((a) => (a.codigo === alerta.codigo ? ligar : a.ligado))
      .map((a) => a.codigo);
    definirGravando(true);
    definirErro(null);
    try {
      definirMeus(
        await api<MeusAlertas>("/whatsbi/meus-alertas", {
          method: "PUT",
          corpo: { ligados: proximos },
        }),
      );
    } catch (falha) {
      definirErro(
        falha instanceof ErroDaApi ? falha.message : "Falha inesperada.",
      );
    } finally {
      definirGravando(false);
    }
  }

  return (
    <details className="text-[11px] text-tinta-2">
      <summary className="cursor-pointer text-tinta-3 select-none">
        Alertas no WhatsApp ·{" "}
        {ligados.length === 0
          ? "nenhum ligado"
          : `${ligados.length} ${ligados.length === 1 ? "ligado" : "ligados"}`}
      </summary>
      <div className="mt-1.5 grid gap-1.5 rounded-botao border border-linha bg-painel-2 px-2 py-2">
        {!meus.disponivel && meus.motivo && (
          <p className="text-tinta-3">{meus.motivo}</p>
        )}
        <ul aria-label="Alertas no WhatsApp" className="grid gap-1.5">
          {meus.alertas.map((a) => (
            <li key={a.codigo}>
              <label className="flex items-start gap-1.5">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={a.ligado}
                  disabled={gravando || (!meus.disponivel && !a.ligado)}
                  onChange={(e) => void trocar(a, e.target.checked)}
                />
                <span>
                  <span className="font-semibold text-tinta">{a.nome}</span>
                  <span className="block text-tinta-3">{a.descricao}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
        <p className="text-[10.5px] text-tinta-3">
          Os números são os das telas de Operação e SENATRAN, no seu recorte.
          Nenhum alerta sai {meus.silencio}. Para desligar todos pelo WhatsApp,
          responda PARAR.
        </p>
        {erro && (
          <p role="alert" className="text-vermelho">
            {erro}
          </p>
        )}
      </div>
    </details>
  );
}
